<template>
  <BittsModal
    class="c-view-reason-modal"
    :class="{ 'c-view-reason-modal--has-reinvite': showReinvite }"
    :width="480"
    primary-button-text="Got it"
    primary-button-type="neutral"
    :show-secondary-button="showReinvite"
    :use-mask-to-close="true"
    data-testid="view-reason-modal"
    @action="handleClose"
    @closed="handleClose"
  >
    <template v-if="showReinvite" #secondary-button>
      <BittsButton
        text="Invite again"
        size="large"
        type="neutral"
        variant="outline"
        data-testid="view-reason-reinvite"
        @click="handleReinvite"
      />
    </template>
    <template #header>
      <div class="c-view-reason-modal__header">
        <BittsAvatarStack
          v-if="partner"
          :org-list="[
            currentOrgWithInitials,
            { ...partner, showInitials: true },
          ]"
          :transparent="true"
          :show-initials="true"
          size="medium"
        />
        <h2 class="c-view-reason-modal__title">
          {{ partnerName }} declined your partnership request
        </h2>
        <p v-if="declinerName" class="c-view-reason-modal__subtitle">
          {{ declinerName }} at {{ partnerName }} declined your partnership
          request.
        </p>
      </div>
    </template>

    <div class="c-view-reason-modal__message">
      <div v-if="hasReason" class="c-view-reason-modal__from">
        <BittsAvatar :org="partner" size="x-small" :show-initials="true" />
        <span class="text-base"
          >Message from {{ declinerName || 'your partner' }}</span
        >
      </div>
      <div class="c-view-reason-modal__block" data-testid="view-reason-content">
        <p class="c-view-reason-modal__reason">
          {{ reasonLabel }}
        </p>
        <p v-if="note" class="c-view-reason-modal__note">{{ note }}</p>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsAvatarStack,
  BittsButton,
  BittsModal,
} from '@crossbeam/bitts';

import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import {
  DECLINE_REASON_LABELS,
  DECLINE_REASON_PREFER_NOT_TO_SAY,
} from '@/constants/decline_reasons';

const {
  partnerName = '',
  declinerName = '',
  reason = null,
  note = null,
  partner = null,
  showReinvite = false,
} = defineProps<{
  partnerName?: string;
  declinerName?: string | null;
  reason?: string | null;
  note?: string | null;
  partner?: {
    name?: string;
    logo_url?: string | null;
    clearbit_domain?: string | null;
    domain?: string | null;
  } | null;
  showReinvite?: boolean;
}>();

const emit = defineEmits<{ close: []; reinvite: [] }>();

const { currentOrg } = useAuth();
const currentOrgWithInitials = computed(() => ({
  ...currentOrg.value,
  showInitials: true,
}));

// A concrete reason renders its label; "prefer not to say" and a missing reason
// both show a plain "No reason provided" message in the red callout, and the
// "Message from …" header is hidden (there is no message to attribute).
const hasReason = computed(
  () => !!reason && reason !== DECLINE_REASON_PREFER_NOT_TO_SAY,
);
const reasonLabel = computed(() =>
  reason && reason !== DECLINE_REASON_PREFER_NOT_TO_SAY
    ? (DECLINE_REASON_LABELS[reason] ?? reason)
    : 'No reason provided',
);

function handleClose() {
  emit('close');
}

function handleReinvite() {
  emit('reinvite');
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-view-reason-modal.o-bitts-modal.bitts-modal--primary
  :deep(.o-bitts-modal__content) {
  @apply m-0 p-16;
}

/* Single "Got it" button: the default footer is justify-between, which parks a
   lone button on the left. Right-align it. */
.c-view-reason-modal.o-bitts-modal.bitts-modal--primary
  :deep(.o-bitts-modal__footer) {
  @apply justify-end;
}

/* With "Invite again": keep it on the left and "Got it" on the right. */
.c-view-reason-modal--has-reinvite.o-bitts-modal.bitts-modal--primary
  :deep(.o-bitts-modal__footer) {
  @apply justify-between;
}
.c-view-reason-modal__header {
  @apply flex flex-col items-center gap-8 w-full;
}

/* Ant Design override, important unavoidable here — match the 48px company
   avatars used in the decline modal. */
.c-view-reason-modal__header :deep(.ant-avatar) {
  width: 48px !important;
  height: 48px !important;
  line-height: 48px !important;
}
.c-view-reason-modal__title {
  @apply text-neutral-text-strong text-xl font-bold text-center;
}
.c-view-reason-modal__subtitle {
  @apply text-neutral-text text-center;
}
.c-view-reason-modal__message {
  @apply flex flex-col gap-10;
}
.c-view-reason-modal__from {
  @apply flex items-center gap-8 text-neutral-text-strong text-sm font-bold;
}
.c-view-reason-modal__block {
  @apply rounded-lg bg-danger-background-weak/50 p-16 flex flex-col gap-4;
}
.c-view-reason-modal__reason {
  @apply text-danger-text font-bold;
}
.c-view-reason-modal__note {
  @apply text-danger-text-strong;
}
</style>
